import { CircularProgress, createStyles, makeStyles } from "@material-ui/core";
import Dialog from "components/Dialog";
import useText from "constants/text";
import { formError, ProductContext, ProductEmptyForm } from "contexts/ProductContext";
import { Product } from "generated/graphql";
import React from "react";
import { ArticleForm } from "./ArticleForm";
import { CategoryForm } from "./CategoryForm";
import { CustomStepper } from "./CustomStepper";
import { RecapForm } from "./RecapForm";

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
    },
    stepper: {
      width: "100%",
    },
    forms: {
      width: "100%",
      display: "flex",
      justifyContent: "center",
    },
  })
);

interface CreateArticleDialogProps {
  open: boolean;
  handlerNewArticleClose: any;
  confirmCreation?: (product: Product, image: any) => void;
  isUpdate?: boolean;
  productToUpdate?: Product;
  loading?: boolean
}

export function CreateArticleDialog({
  open,
  handlerNewArticleClose,
  confirmCreation,
  isUpdate,
  productToUpdate,
  loading
}: CreateArticleDialogProps) {
  const styles = useStyles();
  const [stepped, setStepped] = React.useState(0);
  const { product, setProduct, setError, error, image, setImage } = React.useContext(ProductContext);

  const text = useText();

  React.useEffect(() => {
    if (isUpdate) {
      setProduct(productToUpdate);
      setImage(productToUpdate?.photoUrl);
    } else {
      setProduct(ProductEmptyForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdate]);

  React.useEffect(() => {
    if (!open) {
      setProduct(ProductEmptyForm);
      setImage(null);
      setError(formError);
      setStepped(0);
    }
  }, [open]);

  const SteppedForm = React.useCallback(() => {
    switch (stepped) {
      case 0:
        return <ArticleForm />;
      case 1:
        return <CategoryForm />;
      case 2:
        return <RecapForm />;
      default:
        return <ArticleForm />;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepped]);

  const isFinalStep = stepped === 2;
  const isFirstStep = stepped === 0;

  const goNextStepper = () => (stepped >= 2 ? 2 : stepped + 1);
  const goPreviousStepper = () => (stepped <= 0 ? 0 : stepped - 1);
  const isGreatterThanZero = (value: any, test = 0) => {
    const num = Number(value);
    return typeof num === "number" && num > test;
  };
  React.useEffect(() => {
    if (stepped === 0) {
      setError((prev: any) => ({
        ...prev,
        name: { ...prev.name, error: product?.name?.length <= 0 },
        reference: { ...prev.reference, error: product.reference?.length <= 0 },
        description: { ...prev.description, error: product.description.length <= 0 },
      }));
    }
    if (stepped === 1) {
      setError((prev: any) => ({
        ...prev,
        category: { error: product.category === null },
        value: { ...prev.value, error: !isGreatterThanZero(product.value, 24) },
        volume: { ...prev.volume, error: !isGreatterThanZero(product.volume) },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, stepped]);

  const errorHandled = () =>
    stepped === 0
      ? error.name.error || error.reference.error || error.description.error
      : error.category.error || error.value.error;

  const handleConfirmModal = () => {
    setError((prev: any) => ({ ...prev, submitted: true }));
    if (isFinalStep) {
      setStepped(0);
      confirmCreation(product, image);
    } else if (!errorHandled()) {
      setError((prev: any) => ({ ...prev, submitted: false }));
      setStepped(goNextStepper);
    }
  };
  const handleCloseModal = (props: string) => {
    if (isFirstStep || ["icon", "escapeKeyDown", "backdropClick"].includes(props)) {
      setImage(null);
      setProduct(ProductEmptyForm);
      handlerNewArticleClose();
      setError((prev: any) => ({ ...prev, submitted: false }));
    } else {
      setStepped(goPreviousStepper);
    }
  };

  return (
    <Dialog
      open={open}
      title={text.text.catalog.new_article}
      maxWidth="lg"
      cancelButtonTitle={isFirstStep ? text.text.all.cancel : text.text.all.previous}
      confirmButtonTitle={isFinalStep ? text.text.all.confirm : text.text.all.next}
      handleConfirm={handleConfirmModal}
      handleClose={handleCloseModal}
      height="600px"
      PrimaryButtonProps={loading ? { endIcon: <CircularProgress size={30} /> } : {}}
      disableConfirmButton={loading}
    >
      <div className={styles.root}>
        <div className={styles.stepper}>
           <CustomStepper
            stepperList={["Article", "Detail", "Recap"]}
            stepped={stepped}
            onStepperClick={(index) => {
              if (!errorHandled() || stepped > index) setStepped(index);
            }}
          /> 
        </div>
        <div className={styles.forms}>
          <SteppedForm />
        </div>
      </div>
    </Dialog>
  );
}
